import { CopyAll, Delete, Save } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Divider,
    FormControl,
    FormLabel,
    IconButton,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import type { ICompanySettingsSlack } from '@sharedInterfaces/ICompanySettings';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import setSlackConfig from '@src/APIs/graphQl/Company/setSlackConfig';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import { stageContext } from '@src/globals';
import type Company from '@src/Objects/Company';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

const slackSigningSecretRegex = /^[a-f0-9]{32}$/;

interface SlackSettingsTabProps {
    slack: ICompanySettingsSlack;
    setCompany: (company: Company) => void;
}
export default function SlackSettingsTab({ slack, setCompany }: SlackSettingsTabProps) {
    const showErrorDialog = useErrorDialog();

    const lang = useSelector((state: AppState) => state.employee.language);
    const companyId = useSelector((state: AppState) => state.company.companyId);
    const langStrings = getLanguageTexts(lang).settings;
    const [slackToken, setSlackToken] = React.useState(slack.tokenIsSet ? '<INSERT_VALUE>' : '');
    const [slackSigningSecret, setSlackSigningSecret] = React.useState(
        slack.signingSecretIsSet ? '<INSERT_VALUE>' : '',
    );
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);

    const endpoint = `${stageContext.apiGatewayRestDomain}/v1/slack/events/${companyId}`;

    const onDelete = () => {
        setSaving(true);
        setSaved(false);
        setSlackConfig('', '')
            .then((c) => {
                setSaved(true);
                setCompany(c);
            })
            .catch((ex) => showErrorDialog(ex))
            .finally(() => {
                setSaving(false);
            });
    };
    const onSave = () => {
        setSaving(true);
        setSaved(false);
        setSlackConfig(slackToken, slackSigningSecret)
            .then((c) => {
                setSaved(true);
                setCompany(c);
            })
            .catch((ex) => showErrorDialog(ex))
            .finally(() => {
                setSaving(false);
            });
    };
    return (
        <Paper sx={{ mt: 3, p: 3, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {langStrings.slack}
                </Typography>
                <Divider />

                {saved && <Alert severity="success">{langStrings.savingSuccessfull}</Alert>}

                <FormControl>
                    <FormLabel>{langStrings.slackToken}</FormLabel>
                    <TextField
                        name="slackToken"
                        type="password"
                        value={slackToken}
                        onChange={(e) => setSlackToken(e.target.value)}
                        size="small"
                    />
                </FormControl>

                <FormControl>
                    <FormLabel>{langStrings.slackSigningSecret}</FormLabel>
                    <TextField
                        name="slackSigningSecret"
                        type="password"
                        value={slackSigningSecret}
                        onChange={(e) => setSlackSigningSecret(e.target.value)}
                        size="small"
                        error={!slackSigningSecretRegex.test(slackSigningSecret) && slackSigningSecret !== ''}
                        helperText={
                            !slackSigningSecretRegex.test(slackSigningSecret) && slackSigningSecret !== ''
                                ? langStrings.slackSigningSecretError
                                : ''
                        }
                    />
                </FormControl>

                <FormControl>
                    <FormLabel>Endpoint</FormLabel>
                    <TextField
                        value={endpoint}
                        disabled={true}
                        size="small"
                        variant="outlined"
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <IconButton
                                    title={langStrings.oftenUsed.copy}
                                    onClick={() => copyToClipboard(endpoint)}
                                >
                                    <CopyAll />
                                </IconButton>
                            ),
                        }}
                    />
                </FormControl>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {slack.tokenIsSet && (
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<Delete />}
                            disabled={saving}
                            onClick={onDelete}
                        >
                            {langStrings.oftenUsed.delete}
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<Save />}
                        disabled={
                            slackToken === '' ||
                            slackToken === '<INSERT_VALUE>' ||
                            !slackSigningSecretRegex.test(slackSigningSecret) ||
                            saving
                        }
                        onClick={onSave}
                    >
                        {langStrings.oftenUsed.save}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
}

function copyToClipboard(link: string) {
    navigator.clipboard.writeText(link);
}
